import {
  Button,
  Comments,
  Email,
  TextInput,
  YesNo,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import {
  CheckboxInput,
  DatepickerInput,
  RadioButton,
} from '@components/form/baseInputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { DataPrivacyComplaintSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'

const CONCERNS = [
  {
    key: 'access',
    label: 'Issue with access to personal data (subject access request)',
  },
  { key: 'inaccurate', label: 'Inaccurate or incomplete personal data' },
  {
    key: 'unlawful',
    label: 'Unlawful processing/ lack of lawful basis',
  },
  { key: 'marketing', label: 'Marketing communications/ cookies' },
  { key: 'disclosure', label: 'Data sharing or disclosure' },
  { key: 'breach', label: 'Security/ data breach' },
  { key: 'retention', label: 'Data retention (kept for too long)' },
  { key: 'transparency', label: 'Transparency / privacy information' },
  { key: 'all', label: 'All of the above' },
]

const RELATIONSHIPS = ['Customer', 'Employee', 'Contractor', 'Other']

const RelationshipRadios = () => {
  const { formState, getFieldState } = useFormContext()
  const { error } = getFieldState('relationship', formState)

  return (
    <div className="mb-5">
      <p className="font-medium my-5">Relationship to the organisation</p>
      <div className="flex flex-col gap-2">
        {RELATIONSHIPS.map((option) => (
          <RadioButton
            key={option}
            name="relationship"
            label={option}
            value={option}
            errorDisabled
          />
        ))}
      </div>
      {error && (
        <p className="text-red text-tiny mt-1" role="alert">
          {error.message}
        </p>
      )}
    </div>
  )
}

const SubmittingForRadios = () => {
  const { formState, getFieldState } = useFormContext()
  const { error } = getFieldState('submitting_for', formState)

  return (
    <div className="mb-5">
      <p className="font-medium my-5">
        Are you submitting this complaint for yourself or on behalf of someone
        else?
      </p>
      <div className="flex flex-col gap-2">
        <RadioButton
          name="submitting_for"
          label="For me"
          value="for_me"
          errorDisabled
        />
        <RadioButton
          name="submitting_for"
          label="For someone else"
          value="for_someone_else"
          errorDisabled
        />
      </div>
      {error && (
        <p className="text-red text-tiny mt-1" role="alert">
          {error.message}
        </p>
      )}
    </div>
  )
}


const DataPrivacyComplaint = ({
  content,
  tracking,
  brand,
}: {
  content: any
  tracking: any
  brand: string
}) => {
  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    full_name: '',
    email: '',
    relationship: '',
    submitting_for: '',
    representative_details: '',
    concern_access: false,
    concern_inaccurate: false,
    concern_unlawful: false,
    concern_marketing: false,
    concern_disclosure: false,
    concern_breach: false,
    concern_retention: false,
    concern_transparency: false,
    concern_all: false,
    description: '',
    issue_date: '',
    issue_date_input: '',
    dealership: '',
    dealership_details: '',
    impact: '',
    outcome: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(DataPrivacyComplaintSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const dealershipTerm = brand === 'lexus' ? 'centre' : 'dealership'

  const watchSubmittingFor = methods.watch('submitting_for')
  const watchDealership = methods.watch('dealership')
  const watchConcernAll = methods.watch('concern_all')
  const watchIndividualConcerns = methods.watch(
    CONCERNS.filter((c) => c.key !== 'all').map((c) => `concern_${c.key}`) as any,
  ) as boolean[]
  const skipAllSyncRef = useRef(false)

  // concern_all checked/unchecked → sync all individual concerns
  useEffect(() => {
    if (skipAllSyncRef.current) {
      skipAllSyncRef.current = false
      return
    }
    CONCERNS.filter((c) => c.key !== 'all').forEach((c) => {
      methods.setValue(`concern_${c.key}` as any, watchConcernAll)
    })
  }, [watchConcernAll])

  // any individual unchecked while concern_all is true → uncheck concern_all only
  useEffect(() => {
    if (watchConcernAll && watchIndividualConcerns.some((v) => !v)) {
      skipAllSyncRef.current = true
      methods.setValue('concern_all', false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchIndividualConcerns.join(',')])

  const onSubmit = async (data: any) => {
    console.log('onSubmit fired — raw RHF data:', data)

    const selectedConcerns = CONCERNS.filter(
      (c) => data[`concern_${c.key}`],
    ).map((c) => c.label)

    setSubmitResponse('')

    const parsedData = {
      full_name: data.full_name,
      email: data.email,
      relationship: data.relationship,
      submitting_for: data.submitting_for,
      ...(data.submitting_for === 'for_someone_else' && {
        representative_details: data.representative_details,
      }),
      concern: selectedConcerns,
      description: data.description,
      issue_date: data.issue_date,
      dealership: data.dealership === 'true',
      ...(data.dealership === 'true' && {
        dealership_details: data.dealership_details,
      }),
      impact: data.impact,
      outcome: data.outcome,
      is_lexus: data.is_lexus,
    }

    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/data-privacy-complaint`,
      headers: { 'Content-Type': 'application/json' },
      data: parsedData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep subtitle={content.details.subtitle} step={1} active={true}>
          <FormProvider {...methods}>
            <Form
              onSubmit={methods.handleSubmit(onSubmit, (errors) =>
                console.log('validation errors:', errors),
              )}
            >
              <TextInput name="full_name" label="Full Name" />
              <Email />

              <RelationshipRadios />
              <SubmittingForRadios />

              {watchSubmittingFor === 'for_someone_else' && (
                <TextInput
                  name="representative_details"
                  label="Full name of representative and contact details"
                />
              )}

              <div className="mb-5">
                <p className="font-medium my-5">
                  What best describes your concern? (select all that apply)
                </p>
                {CONCERNS.map((concern) => (
                  <CheckboxInput
                    key={concern.key}
                    name={`concern_${concern.key}`}
                    label={concern.label}
                    hideError
                  />
                ))}
                {methods.formState.errors.concern_access && (
                  <p className="text-red text-tiny mt-2" role="alert">
                    {methods.formState.errors.concern_access.message as string}
                  </p>
                )}
              </div>

              <Comments
                name="description"
                label="Please describe what happened (what, when, where, who was involved, include dates and any references you may have)"
                characterLimit={2000}
              />

              <DatepickerInput
                name="issue_date"
                label="On what date did the issue first occur?"
                maxDate={new Date()}
                minDate={new Date(2000, 0, 1)}
              />

              <YesNo
                name="dealership"
                label={`Have you spoken to a ${dealershipTerm} about this issue?`}
              />

              {watchDealership === 'true' && (
                <Comments
                  name="dealership_details"
                  label={`If yes, which ${dealershipTerm} have you spoken with? Please include details of who you spoke with and when.`}
                  characterLimit={500}
                />
              )}

              <Comments
                name="impact"
                label="How has this affected you?"
                characterLimit={500}
              />

              <Comments
                name="outcome"
                label="What outcome are you seeking? (e.g. explanation, correction, deletion, apology, stop processing, etc)"
                characterLimit={500}
              />

              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'data-privacy-complaint'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  if (!content || !content.attributes?.active) {
    return { notFound: true }
  }

  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: { destination: '/maintenance', permanent: false },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'complaint-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default DataPrivacyComplaint
